import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Grid, IconButton } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import DownloadIcon from '@mui/icons-material/Download';
import LoopIcon from '@mui/icons-material/Loop';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { DateRangePickerSelect } from '../../../../reusable-components/date-range-picker';
import { ConfirmDialog } from '../../../../reusable-components/confirm-dialog';
import axiosInstance from '../../../../utils/axios';
import Iconify from '../../../../reusable-components/iconify';
import { fMonthDayTimeYear } from '../../../../utils/formatTime';
import { objectToFormData } from '../../../../api/utils';

export default function InvalidInvoicesView() {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isReprocessingInvoices, setIsReprocessingInvoices] = useState(false);
  const [reprocessInvoices, setReprocessInvoices] = useState(false);
  const [selectedInvoicesToReprocess, setSelectedInvoicesToReprocess] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();

  const columns = [
    {
      field: 'vendor',
      headerName: 'Vendor',
      flex: 1,
      minWidth: 120,
      type: 'customSelect',
    },
    {
      field: 'orderNumber',
      headerName: 'Order Number',
      flex: 1,
      minWidth: 120,
      type: 'customSelect',
    },
    {
      field: 'vendorInvoiceId',
      headerName: 'Vendor Invoice Id',
      width: 200,
      type: 'customSelect',
    },
    {
      field: 'account',
      headerName: 'Account',
      width: 200,
      type: 'customSelect',
    },
    {
      field: 'error',
      headerName: 'Error',
      width: 200,
      type: 'customSelect',
    },
    {
      field: 'dateAdded',
      headerName: 'Received On',
      width: 160,
      renderCell: (params) =>
        params.value ? (
          <>
            <Iconify clickable={false} icon={'material-symbols:calendar-today-outline'} width={13} height={13} />
            {fMonthDayTimeYear(params.value)} EST
          </>
        ) : (
          '-'
        ),
      type: 'customDate',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      disableReorder: true,
      width: 100,
      valueGetter: (params) => params.row.emailBodySearchShortText,
      renderCell: (params) => (
        <>
          <IconButton
            color="info"
            onClick={() => window.open(`/Api/Admin/ReceivedEmail/DownloadInvalidInvoice?id=${params.id}`, '_blank')}
          >
            <DownloadIcon />
          </IconButton>
          <ConfirmDialog
            icon="refresh"
            title="Reprocess Invoice"
            color="warning"
            content="Are you sure you want to reprocess this invoice?"
            actionButton="Reprocess Invoice"
            tooltip="Reprocess Invoice"
            action={() => handleReprocessInvoices([params.id])}
          />
        </>
      ),
    },
  ];

  const getInvalidInvoices = () => {
    setIsLoading(true);
    axiosInstance.get(`/Admin/ReceivedEmail/ReceivedInvalidInvoices?start=${searchParams.get('start')}&end=${searchParams.get('end')}`)
      .then((res) => setData(res.data))
      .catch(() => enqueueSnackbar('Error fetching invoices', { variant: 'error' }))
      .finally(() => setIsLoading(false));
  };

  const handleReprocessInvoices = (ids) => {
    setIsReprocessingInvoices(true);
    axiosInstance.post(`/Admin/ReceivedEmail/ReprocessInvalidInvoices`, objectToFormData({ ids }))
      .catch(() => enqueueSnackbar('Something when wrong', { variant: 'error' }))
      .finally(() => {
        setSelectedInvoicesToReprocess([]);
        setReprocessInvoices(false);
        setIsReprocessingInvoices(false);
        getInvalidInvoices();
      });
  };

  useEffect(() => {
    getInvalidInvoices();
  }, [searchParams]);

  const customToolbar = () => <Grid container direction="row" alignItems="flex-end" spacing={2}>
    <Grid item>
      <DateRangePickerSelect />
    </Grid>
    <Grid item>
      {selectedInvoicesToReprocess.length > 0 ? <LoadingButton
        variant="outlined"
        size="small"
        color="secondary"
        loading={isReprocessingInvoices}
        onClick={() => handleReprocessInvoices(selectedInvoicesToReprocess)}
        startIcon={<LoopIcon />}
      >
        Confirm Reprocess Invoices
      </LoadingButton> : <Button
        variant="outlined"
        size="small"
        color="secondary"
        onClick={() => setReprocessInvoices(show => !show)}
        startIcon={<LoopIcon />}
      >
        {reprocessInvoices ? "Cancel Reprocess Invoices" : "Reprocess Invoices"}
      </Button>}
    </Grid>
  </Grid>;

  return <Grid item xs={12}>
    <CustomDataGrid
      gridId="admin-invalid-invoice-view"
      boxSX={{ height: 'calc(100vh - 225px)' }}
      key={isReprocessingInvoices ? 1 : 0}
      data={data}
      gridColumns={columns}
      sort={[{ field: 'dateAdded', sort: 'desc' }]}
      isLoading={isLoading}
      disableSelectionOnClick
      CustomLeftToolbar={customToolbar}
      checkboxSelection={reprocessInvoices}
      displayCheckboxRight
      onRowSelectionModelChange={(newSelection) => {
        setSelectedInvoicesToReprocess(newSelection.slice(0, 50));
      }}
      rowSelectionModel={selectedInvoicesToReprocess}
    />
  </Grid>;
}
